import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { financingAPI } from '../../services/api';
import toast from 'react-hot-toast';

const INSTITUTION_LABELS = {
  banque: 'Banque',
  microfinance: 'Institution de microfinance',
  fonds_garantie: 'Fonds de garantie',
  investisseur: 'Investisseur / Capital-risque',
  ong: 'ONG',
  etat: 'État / Programme public',
  autre: 'Autre',
};

const FINANCING_TYPE_LABELS = {
  credit: 'Crédit / Prêt',
  subvention: 'Subvention / Don',
  investissement: 'Investissement en capital',
  garantie: 'Garantie',
  autre: 'Autre',
};

const MATURITY_LABELS = {
  idee: 'Idée',
  prototype: 'Prototype',
  creation: 'Création',
  amorcage: 'Amorçage',
  croissance: 'Croissance',
  developpement: 'Développement',
  expansion: 'Expansion / International',
};

const LIMIT = 12;

const Financing = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    country: '',
    financingType: '',
    maturity: '',
    institutionType: '',
  });

  // Réinitialiser la pagination quand un filtre change
  useEffect(() => { setPage(1); }, [search, filters]);

  const { data: meta } = useQuery('financing-meta', async () => {
    const res = await financingAPI.getMeta();
    return res.data;
  });

  const { data: products = [], isLoading } = useQuery(
    ['financing-products', page, search, filters],
    async () => {
      const params = { page, limit: LIMIT };
      if (search) params.search = search;
      if (filters.country) params.country = filters.country;
      if (filters.financingType) params.financingType = filters.financingType;
      if (filters.maturity) params.maturity = filters.maturity;
      if (filters.institutionType) params.institutionType = filters.institutionType;

      const res = await financingAPI.getProducts(params);
      return Array.isArray(res.data) ? res.data : [];
    },
    {
      keepPreviousData: true,
      onError: () => toast.error('Erreur lors du chargement du catalogue de financement'),
    }
  );

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const resetFilters = () => {
    setSearch('');
    setFilters({ country: '', financingType: '', maturity: '', institutionType: '' });
  };

  const formatAmount = (value, currency) => {
    if (value === null || value === undefined) return null;
    return `${Number(value).toLocaleString('fr-FR')} ${currency || ''}`.trim();
  };

  const hasActiveFilters = search || Object.values(filters).some((v) => v);

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-4xl font-bold text-text mb-4">
            FinConnect
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Découvrez les produits de financement proposés par les banques, institutions de
            microfinance, fonds de garantie, investisseurs et autres structures de financement.
          </p>
        </div>

        {/* Recherche et filtres */}
        <div className="bg-white rounded-xl shadow p-6 mb-8">
          <div className="mb-4">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher un produit, une institution..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <select
              value={filters.country}
              onChange={(e) => handleFilterChange('country', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Tous les pays</option>
              {(meta?.countries || []).map((c) => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </select>

            <select
              value={filters.financingType}
              onChange={(e) => handleFilterChange('financingType', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Tous les types de financement</option>
              {(meta?.financingTypes || []).map((t) => (
                <option key={t} value={t}>{FINANCING_TYPE_LABELS[t] || t}</option>
              ))}
            </select>

            <select
              value={filters.maturity}
              onChange={(e) => handleFilterChange('maturity', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Tous les niveaux de maturité</option>
              {(meta?.maturityLevels || []).map((m) => (
                <option key={m} value={m}>{MATURITY_LABELS[m] || m}</option>
              ))}
            </select>

            <select
              value={filters.institutionType}
              onChange={(e) => handleFilterChange('institutionType', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Tous les types d'institutions</option>
              {(meta?.institutionTypes || []).map((t) => (
                <option key={t} value={t}>{INSTITUTION_LABELS[t] || t}</option>
              ))}
            </select>
          </div>

          {hasActiveFilters && (
            <div className="mt-4 text-right">
              <button
                onClick={resetFilters}
                className="text-sm text-primary-600 hover:text-primary-800 font-medium"
              >
                Réinitialiser les filtres
              </button>
            </div>
          )}
        </div>

        {/* Résultats */}
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">Chargement du catalogue...</p>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-12">
            <h3 className="text-xl font-semibold text-text mb-2">Aucun produit trouvé</h3>
            <p className="text-gray-600">
              Aucun produit de financement ne correspond à vos critères de recherche.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product) => {
              const amountMin = formatAmount(product.amount_min, product.currency);
              const amountMax = formatAmount(product.amount_max, product.currency);
              return (
                <Link
                  key={product.id}
                  to={`/financing/${product.id}`}
                  className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow p-6 block"
                >
                  <div className="flex items-center space-x-2 mb-3">
                    <span className="px-2 py-1 bg-primary-100 text-primary-800 text-xs rounded-full">
                      {FINANCING_TYPE_LABELS[product.financing_type] || product.financing_type}
                    </span>
                    <span className="px-2 py-1 bg-secondary-100 text-secondary-800 text-xs rounded-full">
                      {INSTITUTION_LABELS[product.institution_type] || product.institution_type}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-text mb-1 line-clamp-2">
                    {product.name}
                  </h3>
                  <p className="text-sm text-gray-500 mb-3">{product.institution_name}</p>
                  <p className="text-gray-600 mb-4 line-clamp-3">{product.description}</p>

                  {(amountMin || amountMax) && (
                    <div className="text-sm mb-2">
                      <span className="text-gray-500">Montant : </span>
                      <span className="font-semibold text-text">
                        {amountMin && amountMax ? `${amountMin} - ${amountMax}` : (amountMin || amountMax)}
                      </span>
                    </div>
                  )}

                  {Array.isArray(product.maturity_levels) && product.maturity_levels.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-3">
                      {product.maturity_levels.map((m) => (
                        <span key={m} className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded-full">
                          {MATURITY_LABELS[m] || m}
                        </span>
                      ))}
                    </div>
                  )}
                </Link>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {!isLoading && products.length > 0 && (
          <div className="flex items-center justify-center gap-4 mt-10">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 disabled:opacity-50 hover:bg-gray-50"
            >
              Précédent
            </button>
            <span className="text-gray-600">Page {page}</span>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={products.length < LIMIT}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 disabled:opacity-50 hover:bg-gray-50"
            >
              Suivant
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Financing;
